import React from 'react';
import {
  Phone,
  PhoneCall,
  MessageCircle,
  Mail,
  MessageSquare,
  Users,
  FileText,
  Clock,
  User,
  Calendar,
  MessageSquareText,
} from 'lucide-react';

const typeConfig = {
  phone_call: {
    icon: Phone,
    bg: 'bg-red-100',
    iconColor: 'text-red-600',
    label: 'Phone call',
  },
  whatsapp_call: {
    icon: PhoneCall,
    bg: 'bg-green-100',
    iconColor: 'text-green-600',
    label: 'WhatsApp call',
  },
  whatsapp_message: {
    icon: MessageCircle,
    bg: 'bg-green-100',
    iconColor: 'text-green-600',
    label: 'WhatsApp message',
  },
  email: {
    icon: Mail,
    bg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    label: 'Email',
  },
  sms: {
    icon: MessageSquare,
    bg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    label: 'SMS',
  },
  visit: {
    icon: Users,
    bg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    label: 'In-person visit',
  },
  note: {
    icon: FileText,
    bg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    label: 'Note',
  },
  meeting: {
    icon: Users,
    bg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    label: 'Meeting',
  },
};

const unknownConfig = {
  icon: MessageSquareText,
  bg: 'bg-amber-100',
  iconColor: 'text-amber-600',
  label: 'Communication',
};

const outcomeColors = {
  'Sent & replied': 'bg-green-100 text-green-700',
  'Sent, no reply': 'bg-amber-100 text-amber-700',
  'Not delivered': 'bg-gray-100 text-gray-600 border border-gray-300',
  Answered: 'bg-green-100 text-green-700',
  'No answer': 'bg-[#FAEEDA] text-[#633806]',
  Busy: 'bg-red-100 text-red-700',
  Voicemail: 'bg-blue-100 text-blue-700',
  Connected: 'bg-blue-100 text-blue-700',
};

function getTypeConfig(type) {
  return typeConfig[type] || unknownConfig;
}

function getOutcomeColor(outcome) {
  return outcomeColors[outcome] || 'bg-gray-100 text-gray-600';
}

function formatDuration(seconds) {
  if (seconds == null || seconds === 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('en-GB', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDateLabel(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const fmt = (d) =>
    d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  if (date.toDateString() === today.toDateString()) {
    return `Today — ${fmt(date)}`;
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday — ${fmt(date)}`;
  }
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const CommunicationTimeline = ({ communications = [] }) => {
  const groups = React.useMemo(() => {
    const map = {};
    communications.forEach((comm) => {
      const key = new Date(comm.sent_at).toDateString();
      if (!map[key]) map[key] = [];
      map[key].push(comm);
    });
    return map;
  }, [communications]);

  const sortedDateKeys = Object.keys(groups).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  if (communications.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-200" />
        <p className="text-sm text-gray-500">No communications yet</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {sortedDateKeys.map((dateKey) => {
        const comms = groups[dateKey];
        return (
          <div key={dateKey}>
            {/* Date separator */}
            <div className="flex items-center gap-3 pl-[52px] pt-4 pb-3">
              <span className="text-[11px] text-gray-500 font-medium tracking-[0.4px] whitespace-nowrap">
                {getDateLabel(comms[0].sent_at)}
              </span>
              <div className="h-px flex-1 bg-gray-100" />
            </div>

            {comms.map((comm, idx) => {
              const isLastInGroup = idx === comms.length - 1;
              const config = getTypeConfig(comm.type);
              const Icon = config.icon;
              const durationLabel = formatDuration(comm.duration_seconds);

              return (
                <div key={comm.id} className="relative flex gap-4 pb-2">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 border border-gray-200/70 ${config.bg}`}
                    >
                      <Icon className={`w-4 h-4 ${config.iconColor}`} />
                    </div>
                    {!isLastInGroup && (
                      <div
                        className="flex-1 bg-gray-100 my-1 min-h-[8px]"
                        style={{ width: '1.5px' }}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 pb-6">
                    <div className="border border-gray-200 rounded-lg px-4 py-3.5">
                      <div className="flex items-center flex-wrap gap-2 mb-1.5">
                        <span className="text-sm font-medium text-gray-900">
                          {config.label}
                        </span>
                        {comm.outcome && (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getOutcomeColor(comm.outcome)}`}
                          >
                            {comm.outcome}
                          </span>
                        )}
                        {durationLabel && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-gray-500 bg-gray-100">
                            <Clock className="w-3 h-3" />
                            {durationLabel}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-[14px] text-[11px] text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(comm.sent_at)}
                        </span>
                        {comm.agent_name && (
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {comm.agent_name}
                          </span>
                        )}
                      </div>

                      {comm.notes && (
                        <>
                          <div className="mt-2 pt-2 border-t border-gray-100" />
                          <div className="flex items-start gap-1.5 mt-2">
                            <MessageSquareText className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600">{comm.notes}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Empty state */}
      <div className="border border-dashed border-gray-300 rounded-lg p-7 text-center mt-4">
        <MessageCircle size={24} className="mx-auto mb-2 text-gray-300" />
        <p className="text-[12px] text-gray-500">No more activity. Log a call, send a message, or schedule a meeting.</p>
      </div>
    </div>
  );
};

export default CommunicationTimeline;
